import { type ClientFrame, parseServerFrame, type ServerFrame } from './frames.js';

/**
 * How often the client beats (real-time updates §6). Three missed beats mark the agent unreachable,
 * so fifteen seconds gives presence forty-five before it stops offering work to a silent tab.
 */
export const heartbeatIntervalMs = 15_000;

/** The socket, as this module needs it. A test provides its own; the browser provides `WebSocket`. */
export interface RealtimeSocket {
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface SocketEvents {
  onOpen(): void;
  onMessage(data: string): void;
  onClose(): void;
}

export type SocketFactory = (url: string, events: SocketEvents) => RealtimeSocket;

/**
 * The browser's socket, behind the same interface a test uses.
 *
 * `error` is folded into `close`, because that is what it means for this client: `ws` and every
 * browser follow an error with a close, and a second code path for "the socket broke" would only
 * be a second chance to forget one of them.
 */
export const browserSocket: SocketFactory = (url, events) => {
  const socket = new WebSocket(url);
  socket.addEventListener('open', () => events.onOpen());
  socket.addEventListener('message', (message: MessageEvent<unknown>) => {
    if (typeof message.data === 'string') {
      events.onMessage(message.data);
    }
  });
  socket.addEventListener('close', () => events.onClose());
  socket.addEventListener('error', () => events.onClose());
  return {
    send: (data) => socket.send(data),
    close: (code, reason) => socket.close(code, reason),
  };
};

export interface RealtimeConnectionOptions {
  /** Where the gateway is. */
  readonly url: string;
  /** The access token, read at connect and again on every reconnection; never stored here. */
  readonly token: () => string | undefined;
  readonly socket?: SocketFactory;
  readonly heartbeatMs?: number;
  /** What the gateway is told about this device on every beat, read when the beat is sent. */
  readonly deviceReady?: () => boolean;
  readonly onFrame: (frame: ServerFrame) => void;
  readonly onClosed: () => void;
}

export interface RealtimeConnection {
  send(frame: ClientFrame): void;
  /** Closes the socket without reporting it: the caller already knows it asked. */
  close(): void;
}

/**
 * One socket to the gateway, and the two things that belong to a socket rather than to a state:
 * sending the `auth` frame the moment it opens, and beating while it is open.
 *
 * Everything else — when to reconnect, what to subscribe to, when the workspace may stop showing
 * itself as degraded — is a decision, and decisions are in `connection.machine.ts`. This file is
 * the mechanism, so it can be driven by a fake socket in a test without a statechart, and the
 * machine can be driven without a socket.
 */
export function openRealtimeConnection(options: RealtimeConnectionOptions): RealtimeConnection {
  const factory = options.socket ?? browserSocket;
  let beat: ReturnType<typeof setInterval> | undefined;
  let closed = false;

  const stopBeating = () => {
    if (beat !== undefined) {
      clearInterval(beat);
      beat = undefined;
    }
  };

  const socket = factory(options.url, {
    onOpen() {
      const token = options.token();
      if (token === undefined) {
        // No token means no session: there is nothing this socket could be authorised as, and the
        // gateway would close it in ten seconds anyway.
        socket.close(1000, 'no session');
        return;
      }
      socket.send(JSON.stringify({ type: 'auth', token } satisfies ClientFrame));
      beat = setInterval(() => {
        const ready = options.deviceReady?.() ?? false;
        socket.send(
          JSON.stringify({ type: 'heartbeat', deviceReady: ready } satisfies ClientFrame),
        );
      }, options.heartbeatMs ?? heartbeatIntervalMs);
    },
    onMessage(data) {
      const parsed = parseServerFrame(data);
      if (!parsed.ok) {
        console.warn(`realtime: dropped a frame that could not be read (${parsed.why})`);
        return;
      }
      options.onFrame(parsed.frame);
    },
    onClose() {
      stopBeating();
      if (closed) {
        return;
      }
      closed = true;
      options.onClosed();
    },
  });

  return {
    send(frame) {
      socket.send(JSON.stringify(frame));
    },
    close() {
      closed = true;
      stopBeating();
      socket.close(1000, 'the workspace closed the session');
    },
  };
}
